/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { TenantTypesEnum } from "Constants/TenantTypes";
import { IDistributionGroupMember } from "Interfaces/DistributionLists/IDistributionGroupMember";
import { IDistributionListsJoinRequest } from "Interfaces/DistributionLists/IDistributionListsJoinRequest";

import {
  ADD_MICROSOFT_MEMBER_TO_DL,
  ADD_MICROSOFT_MEMBER_TO_DL_FAIL,
  ADD_MICROSOFT_MEMBER_TO_DL_SUCCESS,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_FAIL,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_SUCCESS,
  DISTRIBUTION_LISTS_RESET,
  DISTRIBUTION_LISTS_SEARCH,
  DISTRIBUTION_LISTS_SEARCH_FAIL,
  DISTRIBUTION_LISTS_SEARCH_SUCCESS,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_FAIL,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_SUCCESS,
  FETCH_MICROSOFT_MEMBERS_FOR_DL,
  FETCH_MICROSOFT_MEMBERS_FOR_DL_FAIL,
  FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS,
  MICROSOFT_MEMBER_DL_RESET,
  REMOVE_MICROSOFT_MEMBER_FROM_DL,
  REMOVE_MICROSOFT_MEMBER_FROM_DL_FAIL,
  REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS,
  UPDATE_DISTRIBUTION_LISTS_LISTING_CURRENT_PAGE,
  UPDATE_DISTRIBUTION_LISTS_LISTING_PAGE_SIZE,
  UPDATE_SELECTED_DISTRIBUTION_LIST,
} from "./DistributionListsActionTypes";

/**
 * Action for when searching distribution list.
 * @interface
 */
export interface ISearchDistributionLists {
  type: typeof DISTRIBUTION_LISTS_SEARCH;
  tenant: TenantTypesEnum;
  searchPage: number;
  pageSize: number;
}

/**
 * Action for when distribution lists were searched successfully.
 * @interface
 */
export interface ISearchDistributionListsSuccess {
  type: typeof DISTRIBUTION_LISTS_SEARCH_SUCCESS;
  distributionLists: IDistributionListsJoinRequest[];
  searchPage: number;
  pageSize: number;
  filteredCount: number;
}

/**
 * Action for when loading distribution lists failed.
 * @interface
 */
export interface ISearchDistributionListsFail {
  type: typeof DISTRIBUTION_LISTS_SEARCH_FAIL;
  error: string;
}

/**
 * Action for resetting distribution lists failed.
 * @interface
 */
export interface IResetDistributionLists {
  type: typeof DISTRIBUTION_LISTS_RESET;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_DISTRIBUTION_LISTS_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_DISTRIBUTION_LISTS_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when the dl join request is approved or declined.
 * @interface
 */
export interface IApproveOrDeclineJoinRequest {
  type: typeof APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST;
  requestId: number;
  tenant: string;
  reviewAction: string;
}

/**
 * Action for when the dl join request approve or decline succeed.
 */
export interface IApproveOrDeclineJoinRequestSuccess {
  type: typeof APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_SUCCESS;
}

/**
 * Action for when the dl join request approve or decline fail.
 */
export interface IApproveOrDeclineJoinRequestFail {
  type: typeof APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_FAIL;
  error: string;
}

/**
 * Action for fetching a list of DLs by tenant.
 * @interface
 */
export interface IFetchDistributionListByTenantRequest {
  type: typeof FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST;
  tenant: string;
  pageSize: number;
  pageNumber: number;
}

/**
 * Action for when fetching a list of DLs by tenant is successful.
 * @interface
 */
export interface IFetchDistributionListByTenantRequestSuccess {
  type: typeof FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_SUCCESS;
  distributionListsByTenant: any;
  count: number;
}

/**
 * Action for when fetching a list of DLs by tenant fails.
 * @interface
 */
export interface IFetchDistributionListByTenantRequestFail {
  type: typeof FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_FAIL;
  errorMessage: string;
}

/**
 * Action for fetching the microsoft members of a DL.
 * @interface
 */
export interface IFetchMicrosoftMembersForDLRequest {
  type: typeof FETCH_MICROSOFT_MEMBERS_FOR_DL;
  tenant: string;
  dlAlias: string;
}

/**
 * Action for when fetching the microsoft members of a DL
 * is successful.
 * @interface
 */
export interface IFetchMicrosoftMembersForDLRequestSuccess {
  type: typeof FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS;
  microsoftMembers: IDistributionGroupMember[];
}

/**
 * Action for when fetching the microsoft members of a DL fails.
 * @interface
 */
export interface IFetchMicrosoftMembersForDLRequestFail {
  type: typeof FETCH_MICROSOFT_MEMBERS_FOR_DL_FAIL;
  errorMessage: string;
}

/**
 * Action for updating the user's selected
 * distribution list.
 * @interface
 */
export interface IUpdateSelectedDistributionList {
  type: typeof UPDATE_SELECTED_DISTRIBUTION_LIST;
  distributionListEmail: string;
}

/**
 * Action for adding a microsoft member to a DL.
 * @interface
 */
export interface IAddMicrosoftMemberToDLRequest {
  type: typeof ADD_MICROSOFT_MEMBER_TO_DL;
  tenant: string;
  dlAlias: string;
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Action for when adding a microsoft member to a DL fails.
 * @interface
 */
export interface IAddMicrosoftMemberToDLRequestFail {
  type: typeof ADD_MICROSOFT_MEMBER_TO_DL_FAIL;
  errorMessage: string;
}

/**
 * Action for when adding a microsoft member to a DL succeeds.
 * @interface
 */
export interface IAddMicrosoftMemberToDLRequestSuccess {
  type: typeof ADD_MICROSOFT_MEMBER_TO_DL_SUCCESS;
}

/**
 * Action for removing a microsoft member from a DL.
 * @interface
 */
export interface IRemoveMicrosoftMemberFromDLRequest {
  type: typeof REMOVE_MICROSOFT_MEMBER_FROM_DL;
  tenant: string;
  dlAlias: string;
  memberEmail: string;
}

/**
 * Action for when removing a microsoft member from a DL fails.
 * @interface
 */
export interface IRemoveMicrosoftMemberFromDLRequestFail {
  type: typeof REMOVE_MICROSOFT_MEMBER_FROM_DL_FAIL;
  errorMessage: string;
}

/**
 * Action for when removing a microsoft member from a DL succeeds.
 * @interface
 */
export interface IRemoveMicrosoftMemberFromDLRequestSuccess {
  type: typeof REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS;
}

/**
 * Action for resetting the state for adding/removing Microsoft
 * members to/from distribution lists.
 * @interface
 */
export interface IResetMicrosoftMemberDLs {
  type: typeof MICROSOFT_MEMBER_DL_RESET;
}

/**
 * Action: updating a user's blocked shipping status
 * @interface
 */
export interface IFetchMicrosoftMembersDlSuccess {
  type: typeof FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS;
  microsoftMembers: IDistributionGroupMember[];
}

/**
 * Action creator for DISTRIBUTION_LIST_SEARCH.
 */
export function searchDistributionLists(
  tenant: TenantTypesEnum,
  searchPage: number,
  pageSize: number
): DistributionListsActionTypes {
  return {
    type: DISTRIBUTION_LISTS_SEARCH,
    tenant,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for DISTRIBUTION_LIST_SEARCH_SUCCESS.
 * @param distributionList the distribution list request successfully searched.
 */
export function searchDistributionListsSuccess(
  distributionLists: IDistributionListsJoinRequest[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): DistributionListsActionTypes {
  return {
    type: DISTRIBUTION_LISTS_SEARCH_SUCCESS,
    distributionLists,
    searchPage,
    pageSize,
    filteredCount,
  };
}

/**
 * Action creator for DISTRIBUTION_LIST_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchDistributionListsFail(error: string): DistributionListsActionTypes {
  return {
    type: DISTRIBUTION_LISTS_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for DISTRIBUTION_LISTS_RESET.
 * @returns
 */
export function resetDistributionLists(): DistributionListsActionTypes {
  return {
    type: DISTRIBUTION_LISTS_RESET,
  };
}

/**
 * Action creator for UPDATE_DISTRIBUTION_LIST_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): DistributionListsActionTypes {
  return {
    type: UPDATE_DISTRIBUTION_LISTS_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_DISTRIBUTION_LIST_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): DistributionListsActionTypes {
  return {
    type: UPDATE_DISTRIBUTION_LISTS_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action creator for APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST.
 */
export function approveOrDeclineJoinRequest(
  requestId: number,
  tenant: string,
  approved: boolean
): DistributionListsActionTypes {
  return {
    type: APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST,
    requestId,
    tenant,
    reviewAction: approved ? "Approved" : "Declined",
  };
}

/**
 * Action creator for APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_SUCCESS.
 */
export function approveOrDeclineJoinRequestSuccess(): DistributionListsActionTypes {
  return {
    type: APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_SUCCESS,
  };
}

/**
 * Action creator for APPROVE_DISTRIBUTION_LIST_JOIN_REQUEST_FAIL.
 * @param error the error message from the API.
 */
export function approveOrDeclineJoinRequestFail(error: string): DistributionListsActionTypes {
  return {
    type: APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_FAIL,
    error,
  };
}

/**
 * Action creator for FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST.
 * @param tenant tenant
 * @param pageSize page size
 * @param pageNumber page number
 */
export function fetchDistributionListsByTenant(
  tenant: string,
  pageSize: number,
  pageNumber: number
): DistributionListsActionTypes {
  return {
    type: FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST,
    tenant,
    pageSize,
    pageNumber,
  };
}

/**
 * Action creator for FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_SUCCESS.
 * @param distributionListsByTenant all DLs for a given tenant
 * @param count total number of DLs in that list returned
 */
export function fetchDistributionListsByTenantSuccess(
  distributionListsByTenant: any,
  count: number
): DistributionListsActionTypes {
  return {
    type: FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_SUCCESS,
    distributionListsByTenant,
    count,
  };
}

/**
 * Action creator for FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_FAIL.
 * @param errorMessage error message
 */
export function fetchDistributionListsByTenantFail(
  errorMessage: string
): DistributionListsActionTypes {
  return {
    type: FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for UPDATE_SELECTED_DISTRIBUTION_LIST.
 * @param distributionListEmail the distribution list email
 */
export function updateSelectedDistributionList(
  distributionListEmail: string
): DistributionListsActionTypes {
  return {
    type: UPDATE_SELECTED_DISTRIBUTION_LIST,
    distributionListEmail,
  };
}

/**
 * Action creator for FETCH_MICROSOFT_MEMBERS_FOR_DL.
 * @param tenant tenant name
 * @param dlAlias the distribution list email
 */
export function fetchMicrosoftMembersForDL(tenant: string, dlAlias: string) {
  return {
    type: FETCH_MICROSOFT_MEMBERS_FOR_DL,
    tenant,
    dlAlias,
  };
}

/**
 * Action creator for FETCH_MICROSOFT_MEMBERS_FOR_DL_FAIL.
 * @param errorMessage error message
 */
export function fetchMicrosoftMembersForDLFail(errorMessage: string) {
  return {
    type: FETCH_MICROSOFT_MEMBERS_FOR_DL_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS.
 * @param {IDistributionGroupMember[]} microsoftMembers list of microsoft members in that DL
 * @returns {IFetchMicrosoftMembersDlSuccess} microsoft members
 */
export function fetchMicrosoftMembersForDLSuccess(
  microsoftMembers: IDistributionGroupMember[]
): IFetchMicrosoftMembersDlSuccess {
  return {
    type: FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS,
    microsoftMembers,
  };
}

/**
 * Action creator for ADD_MICROSOFT_MEMBER_TO_DL.
 * @param tenant tenant name
 * @param dlAlias distribution list email
 * @param email the user's email
 * @param displayName the user's display name (first and last name)
 */
export function addMicrosoftMemberToDL(
  tenant: string,
  dlAlias: string,
  email: string,
  displayName: string,
  firstName: string,
  lastName: string
) {
  return {
    type: ADD_MICROSOFT_MEMBER_TO_DL,
    tenant,
    dlAlias,
    email,
    displayName,
    firstName,
    lastName,
  };
}

/**
 * Action creator for ADD_MICROSOFT_MEMBER_TO_DL_FAIL.
 * @param errorMessage error message
 */
export function addMicrosoftMemberToDLFail(errorMessage: string) {
  return {
    type: ADD_MICROSOFT_MEMBER_TO_DL_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for ADD_MICROSOFT_MEMBER_TO_DL_SUCCESS.
 */
export function addMicrosoftMemberToDLSuccess() {
  return {
    type: ADD_MICROSOFT_MEMBER_TO_DL_SUCCESS,
  };
}

/**
 * Action creator for REMOVE_MICROSOFT_MEMBER_FROM_DL.
 * @param tenant tenant name
 * @param dlAlias distribution list email
 * @param memberEmail the user's microsoft email
 */
export function removeMicrosoftMemberFromDL(tenant: string, dlAlias: string, memberEmail: string) {
  return {
    type: REMOVE_MICROSOFT_MEMBER_FROM_DL,
    tenant,
    dlAlias,
    memberEmail,
  };
}

/**
 * Action creator for REMOVE_MICROSOFT_MEMBER_FROM_DL_FAIL
 * @param errorMessage error message
 */
export function removeMicrosoftMemberFromDLFail(errorMessage: string) {
  return {
    type: REMOVE_MICROSOFT_MEMBER_FROM_DL_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS
 */
export function removeMicrosoftMemberFromDLSuccess() {
  return {
    type: REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS,
  };
}

/**
 * Action creator for REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS
 */
export function resetMicrosoftMemberDLs() {
  return {
    type: MICROSOFT_MEMBER_DL_RESET,
  };
}

export type DistributionListsActionTypes =
  | ISearchDistributionLists
  | ISearchDistributionListsSuccess
  | ISearchDistributionListsFail
  | IResetDistributionLists
  | IUpdateCurrentPage
  | IUpdatePageSize
  | IApproveOrDeclineJoinRequest
  | IApproveOrDeclineJoinRequestSuccess
  | IApproveOrDeclineJoinRequestFail
  | IFetchDistributionListByTenantRequest
  | IFetchDistributionListByTenantRequestFail
  | IFetchDistributionListByTenantRequestSuccess
  | IUpdateSelectedDistributionList
  | IFetchMicrosoftMembersForDLRequest
  | IFetchMicrosoftMembersForDLRequestFail
  | IFetchMicrosoftMembersForDLRequestSuccess
  | IAddMicrosoftMemberToDLRequest
  | IAddMicrosoftMemberToDLRequestFail
  | IAddMicrosoftMemberToDLRequestSuccess
  | IRemoveMicrosoftMemberFromDLRequest
  | IRemoveMicrosoftMemberFromDLRequestFail
  | IRemoveMicrosoftMemberFromDLRequestSuccess
  | IResetMicrosoftMemberDLs;
